import dayjs from "dayjs";
import axios from "axios";
import Head from "next/head";
import Image from "next/image";
import mixpanel from "@/utils/mixpanel";
import Modal from "@/components/ui/Modal";
import { useState, useEffect } from "react";
import LeadForm from "@/components/lead-form";
import { GetServerSidePropsContext } from "next";
import Mapper from "./webflow_website_mapper.json";
import ButtonPrimary from "@/components/pages/buttonPrimary";
import { TimerOnlyText } from "@/components/pages/timer/timer";
import DATA from "@/pages/[id]/long-free/long_page_content.json";
import InfoTile, { ExpandableTile } from "@/components/pages/infoTile";
import { IndexListItem, SvgListItem } from "@/components/pages/listItem";
import { DataItem, schema } from "@/types/marketingTypes/longFreejsonDT";
import { Disclaimer } from "@/components/MarketingComponents/MarketingDisclaimer";
import { useRouter } from "next/router";

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { masterclassid } = context.query;
  const { slug } = context.params as { slug: string };
  const slugInLongContent = Mapper[slug as keyof typeof Mapper];
  if (!slugInLongContent || slugInLongContent === "undefined")
    return {
      notFound: true,
    };
  const pageData = (DATA as schema)[slugInLongContent];
  try {
    const { data } = await axios.get(
      `${process.env.NEXT_PUBLIC_BASE_API}masterclasses/${masterclassid}`,
      {
        headers: {
          "Accept-Encoding": "gzip",
        },
      }
    );

    return {
      props: {
        masterclassid,
        masterclass: data.data,
        pageData,
        slug: slugInLongContent,
      },
    };
  } catch (err) {
    return {
      props: {
        masterclassid,
        masterclass: {},
        pageData,
        slug: slugInLongContent,
      },
    };
  }
}
export default function LandingPageFree({
  masterclass,
  pageData,
  slug,
  masterclassid,
}: {
  masterclass: any;
  pageData: DataItem;
  slug: string;
  masterclassid: string;
}) {
  const slots = masterclass?.slots;
  const activeSlot = slots?.find((slot: any) => {
    return slot.active;
  });

  const router = useRouter();
  const source = router?.query?.source;
  const [isLeadModalOpen, setLeadModalOpen] = useState(false);
  const handleLeadModalOpen = (segment: string) => {
    mixpanel.track("enroll_now", {
      type: "free",
      landing_page: "1-field-lp",
      masterclass: masterclass?.title,
      masterclassId: masterclass?.id,
      platform: "tradewise",
      segment: "1_field_" + segment,
    });
    setLeadModalOpen(true);
  };

  useEffect(() => {
    mixpanel.track("marketing_page", {
      page_location: window.location.href,
      page_referrer: document.referrer,
      masterclass: masterclass?.title,
      type: "free",
      landing_page: "1-field-lp",
      masterclassId: masterclassid,
      platform: "tradewise",
    });
  }, []);

  return (
    <>
      <Head>
        <title> {pageData?.head?.title} </title>
        <meta
          name={pageData?.head?.meta?.name}
          content={pageData?.head?.meta?.content}
          key={pageData?.head?.meta?.key}
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','GTM-5G6WR26B');
            `,
          }}
        />
      </Head>
      <noscript>
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=GTM-5G6WR26B"
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
        ></iframe>
      </noscript>
      <main className=" pb-24">
        <Modal
          className="md:w-1/2"
          isOpen={isLeadModalOpen}
          closeModal={() => {
            setLeadModalOpen(false);
          }}
        >
          <LeadForm masterClass={masterclass} />
        </Modal>

        <div className="fixed bottom-0 z-20 p-[20px] px-48 max-md:px-7 w-full flex justify-between items-center bg-white">
          <div>
            <div className=" animate-pulse">
              <b className=" text-[#FF7700]  text-4xl mr-1  to-blue-800 ">
                FREE
              </b>
              <span className=" line-through text-xs text-[#7B7D7C]">₹999</span>
            </div>
            <div className="text-xs text-[#7B7D7C]">
              Offer Expires in{" "}
              <b className="text-black">
                <TimerOnlyText
                  tillDateTime={dayjs().add(15, "minute").toDate()}
                />
              </b>
            </div>
          </div>
          <div>
            <button
              onClick={() => handleLeadModalOpen("footer")}
              className="bg-[#FF7700] md:py-3 block !text-white md:text-lg rounded-lg hover:scale-105 max-md:w-36"
            >
              Register Now For Free
            </button>
          </div>
        </div>
        <div className="p-5 md:px-[110px] md:py-[90px] flex flex-col gap-3">
          <div>
            <h1
              className="font-extrabold text-center max-sm:text-xl max-md:text-3xl"
              dangerouslySetInnerHTML={{ __html: pageData?.headLine }}
            ></h1>
            <p
              className="text-lg md:mt-3 font-medium text-center max-md:text-sm max-sm:mt-2"
              dangerouslySetInnerHTML={{ __html: pageData?.hero_text }}
            ></p>
          </div>
          <div className="flex justify-center items-start max-lg:flex-wrap-reverse xl:gap-10 max-xl:gap-2">
            <Image
              onClick={() => handleLeadModalOpen("hero_image")}
              src={`/${slug}/heroPhoto.webp`}
              className="order-2 w-[600px] max-lg:w-[500px] cursor-pointer max-sm:max-w-[350px] rounded-md"
              alt={slug}
              height={500}
              width={600}
              loading="eager"
              quality={60}
            />
            <div className="order-1">
              <div className=" grid grid-cols-2 grid-rows-2 gap-4 my-5">
                <InfoTile
                  image="/Common/Long-Free/svg/calender.svg"
                  className="!lg:py-5 !lg:w-64"
                >
                  <p className="text-base max-xl:text-xs">DATE</p>
                  <p className="text-base  max-xl:text-xs font-semibold">
                    {dayjs(activeSlot?.startDateTime).format("DD MMMM YYYY")}
                  </p>
                </InfoTile>
                <InfoTile
                  image="/Common/Long-Free/svg/clock.svg"
                  className="!lg:py-5 !lg:w-64"
                >
                  <p className="text-base max-xl:text-xs">TIME</p>
                  <p className="text-base font-semibold max-xl:text-xs">
                    {dayjs(activeSlot?.startDateTime).format("hh:mm A")}
                  </p>
                </InfoTile>
                <InfoTile
                  image="/Common/Long-Free/svg/globe.svg"
                  className="!lg:py-5 !lg:w-64"
                >
                  <p className="text-base max-xl:text-xs">Language</p>
                  <p className="text-base font-semibold max-xl:text-xs">
                    {pageData?.language}
                  </p>
                </InfoTile>
                <InfoTile
                  image="/Common/Long-Free/svg/sand-clock.svg"
                  className="!lg:py-5 !lg:w-64"
                >
                  <p className="text-base max-xl:text-xs">Duration</p>
                  <p className="text-base font-semibold max-xl:text-xs">
                    2+ Hours
                  </p>
                </InfoTile>
              </div>
              <div className="mt-12 max-sm:mt-3">
                <ButtonPrimary
                  onClick={() => handleLeadModalOpen("hero")}
                  className=" bg-[#FF7700] block md:hidden !text-white rounded-2xl border-t-0 border-x-0 border-b-4 border-[#FF4500] shadow-[0px_4px_15px_0px_rgba(255,119,0,0.2)]"
                >
                  Register Now For Free
                </ButtonPrimary>
                <div className="hidden md:flex md:items-center">
                  <button
                    onClick={() => handleLeadModalOpen("hero")}
                    className=" bg-[#FF7700] px-12 py-4 max-2xl:py-4 block !text-white rounded-lg "
                  >
                    Register Now For Free
                  </button>
                  <Image
                    src="/Common/Long-Free/svg/review_people.svg"
                    className="h-10 ml-3"
                    alt="review people"
                    height={120}
                    width={120}
                  ></Image>
                  <div className="ml-3">
                    <div className="flex">
                      {Array.apply(null, Array(5)).map(
                        (value: any, index: number) => {
                          return (
                            <img
                              src="/Common/Long-Free/svg/star.svg"
                              key={index}
                            ></img>
                          );
                        }
                      )}
                    </div>
                    <div className="text-[12px]">14k+ reviews (4.9 of 5)</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="max-sm:mt-2 flex justify-center">
            <div
              className="p-6 bg-[#FFF5ED] border-2 border-dotted border-[#FFD3AD] rounded-xl text-lg max-sm:text-sm max-md:text-base max-sm:p-3 text-center w-[1180px]"
              dangerouslySetInnerHTML={{ __html: pageData?.below_hook_line }}
            ></div>
          </div>
        </div>
        <div className="bg-[#041C33] p-5 md:p-[110px] md:py-[47px] md:items-center md:justify-center">
          <h2 className="text-[21px] text-white md:text-3xl font-extrabold text-center">
            This <b className="text-[#F70]">masterclass</b> is designed for You!
          </h2>
          <div className="mt-5 md:mt-20 flex flex-col md:flex-row gap-5">
            {pageData?.buy_motive.map((reason, ind) => (
              <SvgListItem image="/Common/Long-Free/svg/stars.svg" key={ind}>
                {reason}
              </SvgListItem>
            ))}
          </div>
          <button
            onClick={() => handleLeadModalOpen("designed_for_you")}
            className="bg-[#FF7700] max-sm:text-xl px-10 py-5 md:py-3 text-lg w-full md:w-auto mt-6 md:mt-12 mx-auto block !text-white rounded-lg "
          >
            Register Now For Free
          </button>
        </div>
        {/**svg here */}
        <div className="bg-[#041C33] p-5 md:p-[110px] md:py-[47px]">
          <h2 className="text-[21px] text-white md:text-3xl font-extrabold text-center">
            What Will You <b className="text-[#F70]">Learn?</b>
          </h2>
          <div className="mt-5 md:mt-20 lg:mx-24 md:grid md:grid-cols-2 flex flex-col gap-5 items-center justify-center">
            <IndexListItem index={1} outerClass="md:ml-auto">
              <div
                dangerouslySetInnerHTML={{
                  __html: pageData?.course_outcome.outcome1,
                }}
              ></div>
            </IndexListItem>
            <IndexListItem index={2}>
              <div
                dangerouslySetInnerHTML={{
                  __html: pageData?.course_outcome.outcome2,
                }}
              ></div>
            </IndexListItem>
            <IndexListItem index={3} outerClass="md:ml-auto">
              <div
                dangerouslySetInnerHTML={{
                  __html: pageData?.course_outcome.outcome3,
                }}
              ></div>
            </IndexListItem>
            <IndexListItem index={4}>
              <div
                dangerouslySetInnerHTML={{
                  __html: pageData?.course_outcome.outcome4,
                }}
              ></div>
            </IndexListItem>
          </div>
          <div className="mt-5 flex justify-center">
            <IndexListItem className="col-span-2" index={5}>
              <div
                dangerouslySetInnerHTML={{
                  __html: pageData?.course_outcome.outcome5,
                }}
              ></div>
            </IndexListItem>
          </div>
          <button
            onClick={() => handleLeadModalOpen("course_outcome")}
            className="bg-[#FF7700] max-sm:text-xl px-10 py-5 md:py-3 text-lg w-full md:w-auto mt-6 md:mt-12 mx-auto block !text-white rounded-lg "
          >
            Register Now For Free
          </button>
        </div>
        <div className="bg-[#041C33] p-5 md:p-[110px] md:py-[47px]">
          <div className="md:mt-5 flex lg:flex-row flex-col justify-center items-center w-full gap-5 max-sm:gap-2">
            <h2 className="text-2xl mx-auto lg:hidden text-white lg:text-3xl font-extrabold">
              Meet Your <b className="text-[#F70]">Mentor</b>
            </h2>
            <Image
              src={`/${slug}/teacher-photo.webp`}
              loading="lazy"
              alt={slug}
              height={500}
              width={400}
            />
            <div className="h-full flex flex-col justify-between">
              <h2 className="text-2xl lg:block hidden text-white lg:text-3xl font-extrabold">
                Meet Your <b className="text-[#F70]">Mentor</b>
              </h2>
              <ul className=" bg-white p-[20px] w-full mt-5 max-w-lg rounded flex flex-col gap-5">
                {pageData?.teacher_features &&
                  pageData?.teacher_features.map((feature, idx) => (
                    <li
                      key={idx}
                      dangerouslySetInnerHTML={{ __html: feature }}
                    ></li>
                  ))}
              </ul>
              <button
                onClick={() => handleLeadModalOpen("teacher_cred")}
                className="bg-[#FF7700] max-sm:text-xl px-10 py-5 md:py-3 text-lg mt-6 md:mt-12 block !text-white rounded-lg "
              >
                Register Now For Free
              </button>
            </div>
          </div>
        </div>
        <div className=" p-5 md:py-[47px]">
          <h2 className="text-[21px] md:text-3xl text-center">
            Yes! You will be <span className="text-[#F70]">certified</span> for
            this Masterclass.
          </h2>
          <div className=" w-14 h-1 bg-[#F70] mx-auto mt-1 mb-5"></div>
          <div className="flex flex-col justify-center gap-5 w-full items-center mt-3">
            <Image
              src={`/${slug}/certificate.webp`}
              width={500}
              alt="certificate Image"
              height={500}
              loading="lazy"
            />
          </div>
          <button
            onClick={() => handleLeadModalOpen("certificate")}
            className="bg-[#FF7700] max-sm:text-xl px-10 py-5 md:py-3 text-lg w-full md:w-96 mt-6 md:mt-12 mx-auto block !text-white rounded-lg "
          >
            Register Now For Free
          </button>
          <h2 className="text-[21px] md:text-3xl text-center mt-20">
            Frequently Asked <span className="text-[#F70]">Questions</span>
          </h2>
          <div className=" w-14 h-1 bg-[#F70] mx-auto mt-1 mb-4"></div>
          <div className="w-full flex flex-col gap-5 items-center">
            {pageData?.FAQ_Content.map((faq, ind) => (
              <ExpandableTile header={faq.question} key={ind}>
                {faq.answer}
              </ExpandableTile>
            ))}
          </div>
        </div>
        <Disclaimer source={source as string} classNameOuter="!bg-[#f70]" />
      </main>
    </>
  );
}
